"use client";

import {
  SDUIImage,
  objectFitCoverImageStyles,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Image/SDUIImage";
import { numberToPixel } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/conversions";
import {
  convertServerResponsivePropertyToClientResponsiveProperty,
  getResponsiveValueAtIndex,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import { SDUIComponentRenderBehaviour } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { ImageTextBannerVariant } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Banner/__internal__/ImageTextBanner/__internal__/types";
import {
  assignInlineVars,
  DynamicVariable,
} from "@faire/retailer-visitor-shared/lib/tailwindUtils";
import { cn } from "@faire/web/tools/cn";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { IResponsiveImages } from "@faire/web-api--source/indigofair/data/IResponsiveImages";
import React from "react";

const varWidthTablet: DynamicVariable<"--f-sdui-b-w-md"> = {
  variable: "--f-sdui-b-w-md",
  className: "md:w-(--f-sdui-b-w-md)",
};

const varWidthDesktop: DynamicVariable<"--f-sdui-b-w-lg"> = {
  variable: "--f-sdui-b-w-lg",
  className: "lg:w-(--f-sdui-b-w-lg)",
};

export const BannerImages = ({
  images,
  responsiveImages: serverResponsiveImages,
  variant,
  renderBehaviour,
}: {
  images: IImage[];
  responsiveImages?: IResponsiveImages;
  variant: ImageTextBannerVariant;
  renderBehaviour?: SDUIComponentRenderBehaviour;
}) => {
  const { isTabletAndAbove } = useViewport();

  if (variant === "left" && !isTabletAndAbove) {
    return null;
  }

  const responsiveImages =
    convertServerResponsivePropertyToClientResponsiveProperty(
      images,
      (img) => img,
      serverResponsiveImages
    );

  if (responsiveImages.mobileAndAbove.length === 0) {
    return null;
  }

  const bannerImage = getResponsiveValueAtIndex(
    responsiveImages,
    0,
    IImage.build()
  );

  const tabletImageWidth =
    bannerImage.tabletAndAbove?.width ?? bannerImage.mobileAndAbove?.width;
  const desktopImageWidth =
    bannerImage.desktopAndAbove?.width ?? tabletImageWidth;

  const optimizeImageOptions = {
    mobileAndAbove: {
      height: 160,
    },
    tabletAndAbove: {
      width: tabletImageWidth ?? 584,
    },
    desktopAndAbove: {
      width: desktopImageWidth ?? 584,
    },
  };

  return (
    <div
      className={cn(
        "h-[160px] w-full shrink-0 md:h-auto md:max-w-[50%]",
        varWidthTablet.className,
        varWidthDesktop.className
      )}
      style={assignInlineVars({
        [varWidthTablet.variable]: numberToPixel(tabletImageWidth) ?? "auto",
        [varWidthDesktop.variable]: numberToPixel(desktopImageWidth) ?? "auto",
      })}
    >
      <SDUIImage
        responsiveImage={bannerImage}
        optimizeImageOptions={optimizeImageOptions}
        imageStyles={objectFitCoverImageStyles}
        renderBehaviour={renderBehaviour}
      />
    </div>
  );
};
